import { PublicClientApplication } from '@azure/msal-browser';
import { getMicrosoftSsoConfig } from './api';

const microsoftRedirectStartedKey = 'microsoftSsoRedirectStarted';
const buildTimeClientId = (process.env.REACT_APP_ENTRA_CLIENT_ID || '').trim();
const buildTimeTenantId = (process.env.REACT_APP_ENTRA_TENANT_ID || '').trim();
const defaultRedirectUri = `${window.location.origin}/auth/callback`;
const entraRedirectUri = process.env.REACT_APP_ENTRA_REDIRECT_URI || defaultRedirectUri;
const oidcScopes = ['openid', 'profile', 'email'];
const isSsoDebugEnabled = process.env.NODE_ENV === 'development';
const ssoNotConfiguredMessage = 'Microsoft SSO is not configured for this environment.';

const logSsoDebug = (...args) => {
  if (isSsoDebugEnabled) {
    console.warn(...args);
  }
};

let entraConfig = buildTimeClientId && buildTimeTenantId
  ? {
    clientId: buildTimeClientId,
    tenantId: buildTimeTenantId,
    source: 'build',
  }
  : null;
let entraConfigPromise = null;
let msalInstance = null;
let initializationPromise = null;
let redirectResponsePromise = null;
let loginRedirectInProgress = false;

const loadEntraConfig = async () => {
  if (entraConfig) {
    return entraConfig;
  }

  if (!entraConfigPromise) {
    entraConfigPromise = getMicrosoftSsoConfig()
      .then((response) => {
        const clientId = (response?.client_id || '').trim();
        const tenantId = (response?.tenant_id || '').trim();

        if (!response?.success || !response?.enabled || !clientId || !tenantId) {
          throw new Error(response?.message || ssoNotConfiguredMessage);
        }

        entraConfig = {
          clientId,
          tenantId,
          source: 'runtime',
        };
        return entraConfig;
      })
      .catch((err) => {
        // Permit a retry if configuration becomes available after this request.
        entraConfigPromise = null;
        throw err;
      });
  }

  return entraConfigPromise;
};

const ensureMsalInitialized = async () => {
  const config = await loadEntraConfig();

  if (!msalInstance) {
    msalInstance = new PublicClientApplication({
      auth: {
        clientId: config.clientId,
        authority: `https://login.microsoftonline.com/${config.tenantId}`,
        redirectUri: entraRedirectUri,
        navigateToLoginRequestUrl: false,
      },
      cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
      },
    });
  }

  if (!initializationPromise) {
    initializationPromise = msalInstance.initialize();
  }

  await initializationPromise;
  return { config, instance: msalInstance };
};

export const signInWithMicrosoft = async () => {
  if (loginRedirectInProgress) {
    logSsoDebug('[MSAL] Redirect login already started; ignoring duplicate request');
    return;
  }

  loginRedirectInProgress = true;

  try {
    const { config, instance } = await ensureMsalInitialized();
    logSsoDebug('[MSAL] Starting redirect login', {
      clientId: config.clientId,
      tenantId: config.tenantId,
      configSource: config.source,
      redirectUri: entraRedirectUri,
      currentUrl: window.location.href,
    });

    sessionStorage.setItem(microsoftRedirectStartedKey, '1');
    await instance.loginRedirect({
      scopes: oidcScopes,
      prompt: 'select_account',
      redirectUri: entraRedirectUri,
    });
  } catch (err) {
    loginRedirectInProgress = false;
    sessionStorage.removeItem(microsoftRedirectStartedKey);
    throw err;
  }
};

export const handleMicrosoftRedirect = async () => {
  const { instance } = await ensureMsalInitialized();
  if (!redirectResponsePromise) {
    logSsoDebug('[MSAL] Handling redirect response', {
      currentUrl: window.location.href,
      hash: window.location.hash,
      search: window.location.search,
    });
    redirectResponsePromise = instance.handleRedirectPromise();
  }

  const response = await redirectResponsePromise;

  logSsoDebug('[MSAL] Redirect response received', {
    hasResponse: Boolean(response),
    hasIdToken: Boolean(response?.idToken),
    accountUsername: response?.account?.username,
    accountHomeId: response?.account?.homeAccountId,
  });

  if (response?.account) {
    instance.setActiveAccount(response.account);
  }

  return response;
};

export const acquireMicrosoftIdToken = async () => {
  const { instance } = await ensureMsalInitialized();

  const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
  if (!account) {
    throw new Error('Microsoft did not return a sign-in account.');
  }

  instance.setActiveAccount(account);
  const response = await instance.acquireTokenSilent({
    scopes: oidcScopes,
    account,
    redirectUri: entraRedirectUri,
  });

  logSsoDebug('[MSAL] Silent token response received', {
    hasIdToken: Boolean(response?.idToken),
    accountUsername: response?.account?.username,
  });

  if (!response?.idToken) {
    throw new Error('Microsoft did not return an ID token.');
  }

  return response.idToken;
};

export const completeMicrosoftRedirectLogin = async () => {
  const redirectResponse = await handleMicrosoftRedirect();
  return redirectResponse?.idToken || acquireMicrosoftIdToken();
};

export const wasMicrosoftRedirectStarted = () => (
  sessionStorage.getItem(microsoftRedirectStartedKey) === '1'
);

export const clearMicrosoftRedirectStarted = () => {
  sessionStorage.removeItem(microsoftRedirectStartedKey);
};

export const logoutMicrosoft = async () => {
  let instance;
  try {
    ({ instance } = await ensureMsalInitialized());
  } catch (err) {
    logSsoDebug('[MSAL] Skipping Microsoft logout because SSO is unavailable', {
      message: err.message,
    });
    return;
  }

  const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
  if (account) {
    await instance.logoutRedirect({
      account,
      postLogoutRedirectUri: window.location.origin,
    });
  }
};
